/**
 * Layout measurements published as CSS custom properties on <html> (bottom-nav offset, pager
 * height, …), recomputed on viewport `resize` / `scroll`.
 *
 * On phones those events fire continuously while the browser toolbar collapses and expands during
 * a scroll. Each handler used to measure (getBoundingClientRect / offsetHeight) and then write the
 * property on <html> — unconditionally, and once per handler — which interleaves forced layouts
 * with style invalidations of the root that every element inherits from. Coalescing to one run per
 * frame and writing only real changes keeps the scroll free of that work.
 */

/** Setter for root custom properties that touches the DOM only when a value actually changes. */
export function createRootVarWriter(root: HTMLElement = document.documentElement) {
  return {
    set(name: string, value: string) {
      // Reading the inline declaration back is free (no style or layout work), and comparing with
      // it rather than a private cache stays right when another writer removed the property.
      if (root.style.getPropertyValue(name) === value) return
      root.style.setProperty(name, value)
    },
    remove(name: string) {
      root.style.removeProperty(name)
    },
  }
}

/** Run `fn` at most once per animation frame, however often `schedule` is called. */
export function frameThrottle(fn: () => void) {
  let frame = 0
  return {
    schedule() {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        fn()
      })
    },
    cancel() {
      if (frame) cancelAnimationFrame(frame)
      frame = 0
    },
  }
}
